/**
 * Registration page component - Terminal aesthetic
 */

import { useState, FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import './RegisterPage.css';

interface RegisterPageProps {
  onSwitchToLogin: () => void;
}

export function RegisterPage({ onSwitchToLogin }: RegisterPageProps) {
  const { register, error, clearError, isLoading } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  const validateForm = (): boolean => {
    // Username validation
    if (!username.trim()) {
      setLocalError('Username is required');
      return false;
    }
    if (username.length < 3 || username.length > 50) {
      setLocalError('Username must be 3-50 characters');
      return false;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setLocalError('Username: letters, numbers, underscore, dash only');
      return false;
    }

    // Email validation
    if (!email.trim()) {
      setLocalError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError('Invalid email format');
      return false;
    }

    // Password validation
    if (!password) {
      setLocalError('Password is required');
      return false;
    }
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return false;
    }

    // Confirm password
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!validateForm()) {
      return;
    }

    try {
      await register({
        username: username.trim(),
        email: email.trim(),
        password,
      });
      // On success, AuthContext will update and App.tsx will show main UI
    } catch (err) {
      // Error is set in AuthContext
      console.error('Registration failed:', err);
    }
  };

  const displayError = localError || error;

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <h1>&gt; CREATE ACCOUNT</h1>
          <p>JOIN THE 3D AUDIO SIMULATION</p>
        </div>

        <form className="register-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">USERNAME:</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="CHOOSE USERNAME"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">EMAIL:</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="YOUR@EMAIL.COM"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">PASSWORD:</label>
            <div className="password-input-group">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="MIN 8 CHARACTERS"
                disabled={isLoading}
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">CONFIRM PASSWORD:</label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="RE-ENTER PASSWORD"
              disabled={isLoading}
            />
          </div>

          {displayError && (
            <div className="error-message" role="alert">
              ERROR: {displayError}
            </div>
          )}

          <button
            type="submit"
            className="register-btn"
            disabled={isLoading}
          >
            {isLoading ? 'CREATING ACCOUNT...' : 'REGISTER'}
          </button>

          <div className="register-footer">
            <p>
              ALREADY HAVE ACCOUNT?{' '}
              <button
                type="button"
                className="link-btn"
                onClick={onSwitchToLogin}
                disabled={isLoading}
              >
                LOGIN
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
